import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import IconUI from '../../fragments/icon/IconUI';
import Button from '../../fragments/button/Button';

import { motion } from 'motion/react';

import { SEARCH_SUGGESTIONS } from '../../utils/searchSuggestions';

export default function Search({ isOpen, onClose }) {
  const inputRef = useRef(null);
  const [searchValue, setSearchValue] = useState('');
  const router = useRouter();

  // Auto-focus the input when the overlay opens.
  useEffect(() => {
    if (isOpen && inputRef.current) {
      const timer = setTimeout(() => inputRef.current.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Close the overlay on Escape.
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  function submitSearch(term) {
    const trimmed = term.trim();
    if (!trimmed) return;
    router.push(`/search?q=${encodeURIComponent(trimmed)}`);
  }

  function handleSubmit(e) {
    e.preventDefault();
    submitSearch(searchValue);
  }

  // Suggestions route to their statically generated results page.
  function handleSuggestionClick(suggestion) {
    setSearchValue(suggestion.label);
    router.push(`/search/${suggestion.slug}`);
  }

  // Close when the backdrop (anything outside .Search-inner) is clicked.
  function handleBackdropClick(e) {
    if (!e.target.closest('.Search-inner')) {
      onClose?.();
    }
  }

  return (
    <motion.div
      className="Search"
      onClick={handleBackdropClick}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
    >
      <div className="Search-inner">
        <div className="Search-header">
          <h2 className="Search-heading">Let's help you find it</h2>
          <Button
            icon="close"
            theme="secondary"
            size="small"
            aria-label="Open search"
            onClick={() => onClose()}
          />
        </div>

        <form role="search" className="Search-form" onSubmit={handleSubmit}>
          <div className="Search-field">
            <IconUI icon="search" size="small" />
            <input
              ref={inputRef}
              type="search"
              className="Search-input"
              aria-label="Search"
              placeholder="Search"
              name="s"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </div>
        </form>

        <div className="Search-suggestions">
          <span className="Search-suggestionsLabel headingStrong text--size-s ">
            Suggestions:
          </span>
          <ul className="Search-suggestionsList">
            {SEARCH_SUGGESTIONS.map((suggestion) => (
              <li key={suggestion.slug}>
                <Button
                  buttonLabel={suggestion.label}
                  className="Search-suggestion"
                  theme="toggle"
                  onClick={() => handleSuggestionClick(suggestion)}
                />
              </li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
}
